import { isMoment } from 'moment';
import { FormattedDateProps } from '../FormattedDate';

type DatePiecesType = {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
};

export const isIsoDate = (value: string) => {
  try {
    return new Date(value).toISOString() === value;
  } catch (e) {
    return false;
  }
};

const getDatePieces = (value: InternationalDateArguments['value']): DatePiecesType => {
  if (value instanceof Date) {
    return {
      year: value.getFullYear(),
      month: value.getMonth(),
      day: value.getDate(),
      hours: value.getHours(),
      minutes: value.getMinutes(),
      seconds: value.getSeconds(),
      milliseconds: value.getMilliseconds(),
    };
  }

  if (isMoment(value)) {
    return {
      year: value.year(),
      month: value.month(),
      day: value.date(),
      hours: value.hours(),
      minutes: value.minutes(),
      seconds: value.seconds(),
      milliseconds: value.milliseconds(),
    };
  }

  if (isIsoDate(value)) {
    const date = value.replace(/\D/g, ' ').trim().split(' ').map(Number);

    return {
      year: date[0],
      month: date[1] - 1,
      day: date[2],
      hours: date[3],
      minutes: date[4],
      seconds: date[5],
      milliseconds: date[6],
    };
  }

  const date = new Date(value);
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
    milliseconds: date.getMilliseconds(),
  };
};

interface InternationalDateArguments {
  value: NonNullable<FormattedDateProps['value']>;
  format: NonNullable<FormattedDateProps['format']>;
  timezone: FormattedDateProps['timezone'];
}

const getInternationalDate = ({ value, format, timezone }: InternationalDateArguments): string => {
  const { year, month, day, hours, minutes, seconds, milliseconds } = getDatePieces(value);

  const date = new Date(year, month, day, hours, minutes, seconds, milliseconds);

  let options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

  let formattedDate = new Intl.DateTimeFormat('en-GB', options).format(date);

  if (format === 'i18n-short') {
    if (timezone) {
      formattedDate = new Intl.DateTimeFormat('en-GB', {
        ...options,
        timeZone: timezone,
        timeZoneName: 'short',
      }).format(date);

      return formattedDate.replace(',', '');
    }

    return formattedDate;
  }

  if (format === 'i18n-time') {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    } as const;

    let formattedDate = new Intl.DateTimeFormat('en-GB', options).format(date);

    if (timezone) {
      formattedDate = new Intl.DateTimeFormat('en-GB', {
        ...options,
        timeZone: timezone,
        timeZoneName: 'short',
      }).format(date);
    }

    return formattedDate.replace(' at', ',');
  }

  options = { year: 'numeric', month: 'long', day: 'numeric' };

  if (timezone) {
    formattedDate = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: timezone, timeZoneName: 'short' }).format(
      date,
    );

    return formattedDate.replace(' at', '');
  }

  return new Intl.DateTimeFormat('en-GB', options).format(date);
};

export default getInternationalDate;
